import { useCallback } from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import axios from "axios";
import { useRouter } from "next/router";
import { SubmitHandler, useForm } from "react-hook-form";
import { toast } from "sonner";
import * as yup from "yup";

import {
  Button,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  InputGroup,
  Link,
  VStack,
} from "@chakra-ui/react";

import { Product } from "@/__generated__/graphql";
import Form from "@/components/Form";
import FormErrorMessage from "@/components/FormErrorMessage";
import { BrandedIcon } from "@/components/shared/BrandIcons";
import useReadySearchParams from "@/hooks/useReadySearchParams";
import axiosClient from "@/utils/axiosClient";
import { pathTo } from "@/utils/pathGenerators";

const errorDescription = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    const errorData: unknown | undefined = error.response?.data;

    if (Array.isArray(errorData)) {
      return errorData.join(", ");
    } else {
      return "Unknown error";
    }
  }

  return (error instanceof Error && error.message) || "Unknown error";
};

export const registerSchema = yup.object({}).shape({
  firstName: yup.string().default(null).required("First name is required"),
  lastName: yup.string().default(null).required("Last name is required"),
  email: yup
    .string()
    .email("Email must be a valid email address")
    .default(null)
    .nullable()
    .required("Valid email address required"),

  password: yup
    .string()
    .default(null)
    .required("Password is required")
    .min(8, "Password length must be between 8 and 32 characters")
    .max(32, "Password length must be between 8 and 32 characters"),

  confirmPassword: yup.string().oneOf([yup.ref("password"), null], "Passwords must match"),
});

/** Schemas */
type RegisterValues = yup.InferType<typeof registerSchema>;

function Register({ product }: { product: Product }) {
  const {
    preset_template_hash_id: presetTemplateHashId,
    ollama_model_name: ollamaModelName,
    model,
    next,
    source,
  } = useReadySearchParams();
  const router = useRouter();
  const {
    handleSubmit,
    register,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<RegisterValues>({ resolver: yupResolver(registerSchema) });

  const isRelease = product === Product.Release;

  const isEnterpriseSso = useCallback(
    async (email: string) => {
      /**
       * returns status 200 if this user is part of an enterprise SSO enabled account
       * returns status 422 if this user is not a part of an enterprise SSO enabled account
       * returns other statuses if something unexpectedly went wrong
       */
      const response = await axios.post(
        `${process.env.NEXT_PUBLIC_BACKEND_BASE_URL}/auth/validate_sso_domain`,
        {
          email_address: email,
        },
        {
          validateStatus: status => (status >= 200 && status < 300) || status === 422,
        }
      );

      // If user has an enterprise SSO account, do not let them create a new account
      if (response.status === 200) {
        setError("email", { message: "Contact your admin to be invited to Release" });
        throw new Error("ENTERPRISE_SSO");
      }
    },
    [setError]
  );

  const onSubmit: SubmitHandler<RegisterValues> = useCallback(
    async formData => {
      const { firstName, lastName, email, password } = formData;

      try {
        await isEnterpriseSso(email);
        await axiosClient.post(`${process.env.NEXT_PUBLIC_BACKEND_BASE_URL}/users`, {
          user: {
            first_name: firstName,
            last_name: lastName,
            email,
            password,
            product,
            preset_template_hash_id: presetTemplateHashId,
            ollama_model_name: ollamaModelName,
            model,
            source,
          },
        });

        window.freshpaint?.identify(email, {
          email,
          isOnboarded: false,
        });

        const queryParams: Record<string, string> = {
          email: email,
        };
        if (next) {
          queryParams["next"] = next;
        }
        if (presetTemplateHashId) {
          queryParams["preset_template_hash_id"] = presetTemplateHashId;
        }
        if (ollamaModelName) {
          queryParams["ollama_model_name"] = ollamaModelName;
        }
        router.push(pathTo("/register/verify", { search: queryParams }));
      } catch (error) {
        const isNotEnterpriseSsoError = !(error instanceof Error && error.message == "ENTERPRISE_SSO");
        if (isNotEnterpriseSsoError) {
          toast.error("Oops! An error has occurred.", {
            description: errorDescription(error),
          });
        }
      }
    },
    [isEnterpriseSso, product, presetTemplateHashId, ollamaModelName, model, source, next, router]
  );

  const headerText = () => {
    switch (product) {
      case Product.StandaloneDatasets:
        return (
          <>
            Instant Datasets
            <br />
            Create your free account
          </>
        );
      case Product.ReleaseAi:
        return (
          <>
            Release AI
            <br />
            Create your free account
          </>
        );
      case Product.Release:
      default:
        return "Sign up for Release";
    }
  };

  const authUrl = (provider: string, search?: Record<string, string>) => {
    const baseUrl = `${process.env.NEXT_PUBLIC_BACKEND_BASE_URL}/auth/${provider}`;
    if (isRelease) {
      return pathTo(baseUrl, { search: { ...search, registration: "true" } });
    }
    return pathTo(baseUrl, { search: { ...search, product: product } });
  };

  return (
    <Form errors={errors} id="register" maxW="md" my={12} w="full" onSubmit={handleSubmit(onSubmit)}>
      <VStack spacing={4} w="full">
        <Heading lineHeight="sm" mb={4} size="md" w="full">
          {headerText()}
        </Heading>
        {/* First name */}
        <HStack spacing={4} w="full">
          <FormControl isInvalid={!!errors.firstName}>
            <FormLabel htmlFor="firstName">First Name</FormLabel>
            <InputGroup>
              <Input disabled={isSubmitting} id="firstName" placeholder="First" {...register("firstName")} />
            </InputGroup>
            <FormErrorMessage errors={errors} name="firstName" />
          </FormControl>

          {/* Last name */}
          <FormControl isInvalid={!!errors.lastName}>
            <FormLabel htmlFor="lastName">Last Name</FormLabel>
            <InputGroup>
              <Input disabled={isSubmitting} id="lastName" placeholder="Last" {...register("lastName")} />
            </InputGroup>
            <FormErrorMessage errors={errors} name="lastName" />
          </FormControl>
        </HStack>
        {/* Email address */}
        <FormControl isInvalid={!!errors.email}>
          <FormLabel htmlFor="email">Email address</FormLabel>
          <InputGroup>
            <Input
              disabled={isSubmitting}
              id="email"
              placeholder="email@address.com"
              type="email"
              {...register("email")}
            />
          </InputGroup>
          <FormErrorMessage errors={errors} name="email" />
        </FormControl>
        {/* Password */}
        <FormControl isInvalid={!!errors.password}>
          <FormLabel htmlFor="password">Password</FormLabel>
          <InputGroup>
            <Input
              autoComplete="new-password"
              disabled={isSubmitting}
              id="password"
              placeholder="**********"
              type="password"
              {...register("password")}
            />
          </InputGroup>
          <FormErrorMessage errors={errors} name="password" />
        </FormControl>
        <FormControl isInvalid={!!errors.confirmPassword} mb={4}>
          <FormLabel htmlFor="confirmPassword">Confirm Password</FormLabel>
          <InputGroup>
            <Input
              autoComplete="new-password"
              disabled={isSubmitting}
              id="confirmPassword"
              placeholder="**********"
              type="password"
              {...register("confirmPassword")}
            />
          </InputGroup>
          <FormErrorMessage errors={errors} name="confirmPassword" />
        </FormControl>
        <Button data-testid="submit-button" isLoading={isSubmitting} type="submit" w="full">
          Sign Up
        </Button>
        <Button
          as={Link}
          href={authUrl("google.com", {
            ...(next && { next }),
            ...(presetTemplateHashId && { preset_template_hash_id: presetTemplateHashId }),
            ...(ollamaModelName && { ollama_model_name: ollamaModelName }),
            ...(model && { model }),
            ...(source && { source }),
          })}
          variant="outlineShadow"
          w="full"
        >
          <BrandedIcon brand="google" mr={2} /> Google
        </Button>
        {isRelease && (
          <Button as={Link} href="/login/enterprise" variant="outlineShadow" w="full">
            Enterprise SSO
          </Button>
        )}
      </VStack>
    </Form>
  );
}

export default Register;
